<script setup>
import { ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useCrm } from './composables/useCrm';
import CrmHeader from './components/CrmHeader.vue';
import CrmBoard from './components/CrmBoard.vue';
import CrmListView from './components/CrmListView.vue';
import CrmEmptyState from './components/CrmEmptyState.vue';
import CrmCreateFunnelModal from './components/CrmCreateFunnelModal.vue';
import CrmFunnelSettingsView from './components/CrmFunnelSettingsView.vue';
import CrmFunnelsOverview from './components/CrmFunnelsOverview.vue';
import CrmEditCardModal from './components/CrmEditCardModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const route = useRoute();
const { t } = useI18n();

const {
  pipelines,
  currentPipelineId,
  currentView,
  activePipeline,
  searchQuery,
  selectedAgentId,
  selectedInboxId,
  slaOnlyFilter,
  isSettingsModalOpen,
  isCreateModalOpen,
  createPipeline,
  updatePipeline,
  deletePipeline,
  loadBoardData,
  allAgents,
  allInboxes,
  conversationsByStage,
  filteredConversations,
  columnTotals,
  getDealValue,
  formatCurrency,
  updateDealValue,
  getDealStatus,
  updateDealStatus,
  renameLead,
  getSlaInfo,
  formatDateTime,
  getChannelInfo,
  getSourceInfo,
  getTeamInfo,
  moveConversationToStage,
  assignToMe,
  resolveConversation,
  deleteCardFromKanban,
  duplicateCard,
  updateCardFull,
} = useCrm();

const isEditingFunnel = ref(false);
const activeTab = ref('board'); // 'board' | 'funnels'

const isEditCardModalOpen = ref(false);
const selectedConversationForEdit = ref(null);

// KREACHAT CUSTOM: the expensive paginated conversation fetch only runs here,
// where the CRM board actually needs it — not on every useCrm() consumer.
onMounted(() => {
  loadBoardData();
});

function syncRouteState() {
  const { view, pipelineId } = route.query;

  if (pipelineId) {
    // KREACHAT CUSTOM: pipeline ids are numeric DB ids, route query values are
    // always strings, so compare as strings.
    const exists = pipelines.value.find(
      p => String(p.id) === String(pipelineId)
    );
    if (exists) {
      currentPipelineId.value = exists.id;
    }
  }

  if (view === 'funnels') {
    activeTab.value = 'funnels';
    isEditingFunnel.value = false;
  } else if (view === 'kanban') {
    currentView.value = 'kanban';
    activeTab.value = 'board';
    isEditingFunnel.value = false;
  } else if (view === 'list') {
    currentView.value = 'list';
    activeTab.value = 'board';
    isEditingFunnel.value = false;
  } else {
    activeTab.value = 'board';
  }
}

watch(
  () => route.query,
  () => {
    syncRouteState();
  },
  { immediate: true, deep: true }
);

async function handleCreateFunnel(data) {
  const created = await createPipeline(data);
  isEditingFunnel.value = false;
  activeTab.value = 'board';
  if (created) {
    currentPipelineId.value = created.id;
  }
}

async function handleSavePipeline(updatedPipeline) {
  await updatePipeline(updatedPipeline.id, updatedPipeline);
  isEditingFunnel.value = false;
}

async function handleDeletePipeline(pipelineId) {
  if (confirm(t('CRM.FUNNEL_SETTINGS.DELETE_CONFIRM'))) {
    await deletePipeline(pipelineId);
    isEditingFunnel.value = false;
  }
}

function handleSelectPipeline(pipelineId) {
  currentPipelineId.value = pipelineId;
  activeTab.value = 'board';
  isEditingFunnel.value = false;
}

function handleEditPipeline(pipelineId) {
  currentPipelineId.value = pipelineId;
  isEditingFunnel.value = true;
  activeTab.value = 'board';
}

function handleOpenEditCard(conversation) {
  selectedConversationForEdit.value = conversation;
  isEditCardModalOpen.value = true;
}

function handleSaveCardEdit(updatedData) {
  updateCardFull(updatedData);
  isEditCardModalOpen.value = false;
  selectedConversationForEdit.value = null;
}

const deleteCardDialogRef = ref(null);
const cardIdToDelete = ref(null);

function handleDeleteCard(conversationId) {
  cardIdToDelete.value = conversationId;
  deleteCardDialogRef.value?.open();
}

function handleConfirmDeleteCard() {
  if (cardIdToDelete.value) {
    deleteCardFromKanban(cardIdToDelete.value);
    cardIdToDelete.value = null;
  }
  deleteCardDialogRef.value?.close();
}

function handleDuplicateCard(conversation) {
  duplicateCard(conversation);
}
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-surface-1 overflow-hidden">
    <!-- 1. EMPTY STATE: When account has no funnels -->
    <CrmEmptyState
      v-if="pipelines.length === 0"
      @open-create-modal="isCreateModalOpen = true"
    />

    <!-- 2. FUNNEL SETTINGS / EDIT STAGES VIEW -->
    <CrmFunnelSettingsView
      v-else-if="isEditingFunnel && activePipeline"
      :pipeline="activePipeline"
      :stage-counts="
        Object.fromEntries(
          Object.entries(conversationsByStage).map(([k, v]) => [k, v.length])
        )
      "
      @back="isEditingFunnel = false"
      @save="handleSavePipeline"
      @delete-pipeline="handleDeletePipeline"
    />

    <!-- 3. FUNNELS OVERVIEW: List of funnels when user clicks 'Funis' -->
    <CrmFunnelsOverview
      v-else-if="activeTab === 'funnels'"
      :pipelines="pipelines"
      :stage-counts="
        Object.fromEntries(
          Object.entries(conversationsByStage).map(([k, v]) => [k, v.length])
        )
      "
      @open-create-modal="isCreateModalOpen = true"
      @select-pipeline="handleSelectPipeline"
      @edit-pipeline="handleEditPipeline"
      @delete-pipeline="handleDeletePipeline"
    />

    <!-- 4. MAIN CRM BOARD / LIST VIEW -->
    <template v-else-if="activePipeline">
      <!-- Top Bar Navigation & Filters -->
      <CrmHeader
        v-model:current-pipeline-id="currentPipelineId"
        v-model:current-view="currentView"
        v-model:search-query="searchQuery"
        v-model:selected-agent-id="selectedAgentId"
        v-model:selected-inbox-id="selectedInboxId"
        v-model:sla-only-filter="slaOnlyFilter"
        :pipelines="pipelines"
        :agents="allAgents"
        :inboxes="allInboxes"
        :total-conversations-count="filteredConversations.length"
        @open-settings="isEditingFunnel = true"
      />

      <!-- Main Columns Drag & Drop Board (Kanban View) -->
      <CrmBoard
        v-if="currentView === 'kanban'"
        :pipeline="activePipeline"
        :conversations-by-stage="conversationsByStage"
        :column-totals="columnTotals"
        :get-sla-info="getSlaInfo"
        :get-channel-info="getChannelInfo"
        :get-source-info="getSourceInfo"
        :get-team-info="getTeamInfo"
        :get-deal-value="getDealValue"
        :get-deal-status="getDealStatus"
        :format-currency="formatCurrency"
        :format-date-time="formatDateTime"
        @move-stage="moveConversationToStage"
        @assign-me="assignToMe"
        @resolve="resolveConversation"
        @update-deal-value="updateDealValue"
        @update-deal-status="updateDealStatus"
        @rename-lead="renameLead"
        @delete-card="handleDeleteCard"
        @duplicate-card="handleDuplicateCard"
        @edit-card="handleOpenEditCard"
      />

      <!-- Alternative Spreadsheet List View -->
      <CrmListView
        v-else-if="currentView === 'list'"
        :pipeline="activePipeline"
        :conversations="filteredConversations"
        :get-sla-info="getSlaInfo"
        :get-channel-info="getChannelInfo"
        :get-source-info="getSourceInfo"
        :get-team-info="getTeamInfo"
        :get-deal-value="getDealValue"
        :get-deal-status="getDealStatus"
        :format-currency="formatCurrency"
        :format-date-time="formatDateTime"
        @move-stage="moveConversationToStage"
        @assign-me="assignToMe"
        @resolve="resolveConversation"
        @update-deal-value="updateDealValue"
        @update-deal-status="updateDealStatus"
        @rename-lead="renameLead"
        @delete-card="handleDeleteCard"
        @duplicate-card="handleDuplicateCard"
        @edit-card="handleOpenEditCard"
      />
    </template>

    <!-- CREATE FUNNEL MODAL -->
    <CrmCreateFunnelModal
      :is-open="isCreateModalOpen"
      @close="isCreateModalOpen = false"
      @create="handleCreateFunnel"
    />

    <!-- EDIT LEAD / CARD MODAL -->
    <CrmEditCardModal
      :is-open="isEditCardModalOpen"
      :conversation="selectedConversationForEdit"
      :pipeline="activePipeline"
      :agents="allAgents"
      :inboxes="allInboxes"
      :get-deal-value="getDealValue"
      :get-deal-status="getDealStatus"
      :format-date-time="formatDateTime"
      @close="isEditCardModalOpen = false"
      @save="handleSaveCardEdit"
    />

    <!-- CONFIRM DELETE CARD DIALOG (matching standard contact delete modal) -->
    <Dialog
      ref="deleteCardDialogRef"
      type="alert"
      :title="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TITLE')"
      :description="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.DESCRIPTION')"
      :confirm-button-label="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.CONFIRM')"
      @confirm="handleConfirmDeleteCard"
    />
  </section>
</template>
